/**
 * Game HUD: top resource bar + full-width bottom command bar (identity/stats,
 * context actions, minimap). Pure DOM over the WebGL canvas. Rebuilds its
 * structure only when the selection's shape changes; per-frame it live-updates
 * HP/queue/resource bars so countdowns and damage animate smoothly.
 */
import { icon, RESOURCE_ICON, CLASS_ICON } from "./icons";
import {
  el, hpColor, bar, labeledBar, chip, portrait, costHtml, groupIdentity,
  selectionGrid, constructionPanel, unitStatus,
  RES_ORDER, BUILDING_ICON, type BarRef,
} from "./hud-parts";
import type { World } from "../sim/world";
import type { Registry } from "../data/registry";
import type { Command, Eid } from "../sim/types";
import type { Ident } from "../sim/components";

export interface HudCallbacks {
  onCommand(cmd: Command): void;
  buildMode(): string | null;
  setBuildMode(id: string | null): void;
  focusUnit(eid: Eid): void;
}

/** Population points a transport currently carries (sum of occupants' pop). */
function garrisonUsed(world: World, occupants: Eid[]): number {
  let n = 0;
  for (const occ of occupants) n += world.ident.get(occ)?.population ?? 1;
  return n;
}

export class Hud {
  readonly root: HTMLElement;
  readonly minimap: HTMLCanvasElement;
  private reg: Registry;
  private cbs: HudCallbacks;
  private localPlayer: number;

  private resEls: Record<string, HTMLElement> = {};
  private resWorkers: Record<string, HTMLElement> = {};
  private popEl!: HTMLElement;
  private clockEl!: HTMLElement;
  private clockSpan!: HTMLElement;
  private cmdPanel!: HTMLElement;
  private identity!: HTMLElement;
  private context!: HTMLElement;
  private statusEl!: HTMLElement;
  private structKey = "";

  private statusEl2!: HTMLElement; // toast
  private toastTimer = 0;
  private world: World | null = null;

  private live: {
    hp?: { eid: Eid } & BarRef;
    construction?: { eid: Eid } & BarRef;
    carry?: { eid: Eid; cap: number } & BarRef;
    amount?: { eid: Eid; max: number } & BarRef;
    queueHead?: { eid: Eid } & BarRef;
    costButtons?: { btn: HTMLElement; cost: Record<string, number | undefined>; pop: number }[];
    gathererCount?: { eid: Eid; el: HTMLElement };
    garrison?: { eid: Eid; el: HTMLElement; cap: number };
    status?: { eid: Eid; el: HTMLElement; wrap: HTMLElement };
    builders?: { eid: Eid; el: HTMLElement };
    timeLeft?: { eid: Eid; el: HTMLElement; buildTime: number };
  } = {};

  constructor(reg: Registry, localPlayer: number, cbs: HudCallbacks) {
    this.reg = reg;
    this.cbs = cbs;
    this.localPlayer = localPlayer;
    this.root = el("div", "hud");

    const topbar = el("div", "topbar card");
    for (const r of RES_ORDER) {
      const span = el("span", "res");
      const color = reg.resource(r)?.color ?? "#ddd";
      span.innerHTML =
        `<i class="res-ico" style="color:${color}">${icon(RESOURCE_ICON[r])}</i>` +
        `<span class="res-val"><b>0</b>` +
        `<span class="res-w" title="workers on ${r}">${icon("pickaxe", 9)}<b>0</b></span></span>`;
      this.resEls[r] = span.querySelector("b")!; // first <b> = value
      this.resWorkers[r] = span.querySelector(".res-w b")!;
      topbar.appendChild(span);
    }
    topbar.appendChild(el("div", "divider"));
    const popSpan = el("span", "res pop", `<i class="res-ico">${icon("users")}</i><b>0/0</b>`);
    this.popEl = popSpan.querySelector("b")!;
    topbar.appendChild(popSpan);

    this.clockEl = el("div", "clock card", `${icon("clock")}<span>0:00</span>`);
    this.clockSpan = this.clockEl.querySelector("span")!;

    const bottom = el("div", "bottom-bar");
    this.cmdPanel = el("div", "cmd-panel");
    this.identity = el("div", "identity");
    this.context = el("div", "context");
    this.cmdPanel.append(this.identity, this.context);
    const mmPanel = el("div", "minimap-panel card");
    this.minimap = el("canvas", "minimap") as HTMLCanvasElement;
    this.minimap.width = 284;
    this.minimap.height = 190;
    mmPanel.appendChild(this.minimap);
    bottom.append(this.cmdPanel, mmPanel);

    this.statusEl = el("div", "status");
    this.statusEl2 = el("div", "toast");
    this.root.append(topbar, this.clockEl, bottom, this.statusEl, this.statusEl2);
    document.body.appendChild(this.root);
    this.renderEmpty();
  }

  /** Brief red error message (e.g. missing resources). */
  private showToast(text: string): void {
    this.statusEl2.textContent = text;
    this.statusEl2.classList.add("show");
    this.toastTimer = 150; // ~ frames (cleared in update); also CSS fades
  }

  /** Which requirements a cost+population order is short on, as a readable list. */
  private affordMissing(cost: Record<string, number | undefined>, pop: number): string[] {
    const ps = this.world?.players.get(this.localPlayer);
    if (!ps || !this.world) return [];
    const out: string[] = [];
    for (const [r, amt] of Object.entries(cost)) {
      const need = amt ?? 0;
      const have = Math.floor(ps.resources[r] ?? 0);
      if (have < need) out.push(`${need - have} ${r}`);
    }
    if (pop > 0) {
      let queued = 0;
      for (const [bid, prod] of this.world.producer) {
        if (this.world.owner.get(bid)?.player !== this.localPlayer) continue;
        const civ = this.world.ident.get(bid)?.civ;
        if (!civ) continue;
        for (const it of prod.queue) queued += this.reg.unit(civ, it.unitId).population;
      }
      const avail = ps.populationCap - ps.population - queued;
      if (pop > avail) out.push(`${pop - Math.max(0, avail)} population`);
    }
    return out;
  }

  showStatus(text: string, actions: { label: string; onClick: () => void }[] = []): void {
    this.statusEl.replaceChildren(el("span", "status-title", text));
    for (const action of actions) {
      const button = el("button", "status-action", action.label);
      button.addEventListener("click", action.onClick);
      this.statusEl.appendChild(button);
    }
    this.statusEl.classList.add("overlay");
  }

  update(world: World, sel: Eid[]): void {
    this.world = world;
    // game clock — sim runs at 20 fixed ticks/sec (FIXED_DT = 1/20)
    const secs = Math.floor(world.tick / 20);
    this.clockSpan.textContent = `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, "0")}`;
    const ps = world.players.get(this.localPlayer);
    if (ps) {
      for (const r of RES_ORDER) this.resEls[r].textContent = String(Math.floor(ps.resources[r] ?? 0));
      this.popEl.textContent = `${ps.population}/${ps.populationCap}`;
      // count this player's workers active on each resource type
      const counts: Record<string, number> = { food: 0, wood: 0, gold: 0, stone: 0 };
      for (const [eid, g] of world.gatherer) {
        if (world.owner.get(eid)?.player !== this.localPlayer) continue;
        let type: string | null = null;
        if (g.node !== null && world.resource.has(g.node)) type = world.resource.get(g.node)!.resource;
        else if (g.carrying > 0 && g.resource) type = g.resource;
        if (type && type in counts) counts[type]++;
      }
      // always show the worker tally (even 0), dimmed when none are assigned
      for (const r of RES_ORDER) {
        this.resWorkers[r].textContent = String(counts[r]);
        (this.resWorkers[r].parentElement as HTMLElement).classList.toggle("none", counts[r] === 0);
      }
    }
    const key = this.structuralKey(world, sel);
    if (key !== this.structKey) {
      this.structKey = key;
      this.rebuild(world, sel);
    }
    this.refreshLive(world);
    if (this.toastTimer > 0 && --this.toastTimer === 0) this.statusEl2.classList.remove("show");
  }

  // ---- structure ---------------------------------------------------------

  private owns(world: World, eid: Eid): boolean {
    return world.owner.get(eid)?.player === this.localPlayer;
  }

  private structuralKey(world: World, sel: Eid[]): string {
    const ids = sel.slice().sort((a, b) => a - b);
    let k = ids.join(",") + "|" + (this.cbs.buildMode() ?? "");
    for (const e of ids) {
      const b = world.building.get(e);
      if (b) k += `|b${e}:${b.isFoundation ? 1 : 0}`;
      const p = world.producer.get(e);
      if (p) k += `|q${e}:${p.queue.length}`;
    }
    return k;
  }

  private rebuild(world: World, sel: Eid[]): void {
    this.identity.innerHTML = "";
    this.context.innerHTML = "";
    this.live = {};
    if (sel.length === 0) return this.renderEmpty();
    this.cmdPanel.classList.remove("empty");

    const ownUnits = sel.filter((e) => world.ident.get(e)?.kind === "unit" && this.owns(world, e));
    if (ownUnits.length > 1) {
      this.identity.append(...groupIdentity(world, ownUnits));
      this.context.append(selectionGrid(world, ownUnits, this.cbs.focusUnit), this.stopButton(ownUnits));
      if (ownUnits.every((e) => world.ident.get(e)?.cls === "worker")) this.buildMenu(world, ownUnits);
      return;
    }

    const primary = sel[0];
    const ident = world.ident.get(primary);
    if (!ident) return this.renderEmpty();
    const fb = world.building.get(primary);
    if (fb?.isFoundation && this.owns(world, primary)) return this.buildConstruction(primary, ident);
    this.buildIdentity(world, primary, ident);
    this.buildContext(world, primary, ident);
  }

  /** A compact Stop button (floats to the panel's top-right corner) wired to
   *  cancel the current order for `units`. */
  private stopButton(units: Eid[]): HTMLElement {
    const b = el("button", "stop-corner", `${icon("x", 14)} Stop`);
    b.addEventListener("click", () =>
      this.cbs.onCommand({ type: "stop", player: this.localPlayer, entities: units.slice() }));
    return b;
  }

  /** Foundation under construction: hammer identity + builders/time/cancel. */
  private buildConstruction(eid: Eid, ident: Ident): void {
    const cp = constructionPanel(ident, this.reg,
      () => this.cbs.onCommand({ type: "cancelConstruction", player: this.localPlayer, building: eid }));
    this.identity.append(...cp.identity);
    this.context.append(...cp.context);
    this.live.construction = { eid, ...cp.construction };
    this.live.builders = { eid, el: cp.buildersEl };
    this.live.timeLeft = { eid, el: cp.timeLeftEl, buildTime: cp.buildTime };
  }

  private renderEmpty(): void {
    this.cmdPanel.classList.add("empty");
    const w = this.world;
    let stats = "";
    if (w) {
      let idle = 0, units = 0, buildings = 0;
      for (const [eid, idn] of w.ident) {
        if (w.owner.get(eid)?.player !== this.localPlayer) continue;
        if (idn.kind === "unit") {
          units++;
          const g = w.gatherer.get(eid);
          if (idn.cls === "worker" && !w.move.get(eid)?.intent && (!g || g.node === null)) idle++;
        } else if (idn.kind === "building" && !w.building.get(eid)?.isFoundation) buildings++;
      }
      stats =
        `<div class="empty-stats">` +
        `<div><b style="color:var(--accent)">${idle}</b><span>idle workers</span></div>` +
        `<div><b>${units}</b><span>units</span></div>` +
        `<div><b>${buildings}</b><span>buildings</span></div></div>`;
    }
    this.identity.innerHTML =
      `<div class="hint">${icon("pointer", 26)}` +
      `<div><b>Nothing selected</b><small>click a unit or building · drag to box-select</small></div>` +
      stats +
      `</div>`;
    this.context.innerHTML = "";
  }

  private buildIdentity(world: World, eid: Eid, ident: Ident): void {
    const head = el("div", "id-head");
    head.appendChild(portrait(world, eid, ident));
    const meta = el("div", "id-meta");
    const sub = ident.kind === "unit" ? (ident.cls ?? "unit") : ident.kind;
    meta.innerHTML = `<div class="id-name">${ident.name}</div><div class="id-sub">${sub}</div>`;
    if (ident.kind === "unit") {
      const st = el("div", "id-status", `<span class="dot"></span><span class="t"></span>`);
      meta.appendChild(st);
      this.live.status = { eid, el: st.querySelector(".t") as HTMLElement, wrap: st };
    }
    head.appendChild(meta);
    this.identity.appendChild(head);

    const hp = world.health.get(eid);
    if (hp) {
      const b = labeledBar("hp", "heart", "health");
      this.identity.appendChild(b.el);
      this.live.hp = { eid, ...b };
    }
    const res = world.resource.get(eid);
    if (res) {
      const max = this.reg.resourceNode(ident.defId)?.amount ?? res.amount;
      const ab = labeledBar("amount", RESOURCE_ICON[res.resource] ?? "package", "remaining");
      this.identity.appendChild(ab.el);
      this.live.amount = { eid, max, ...ab };
    }
  }

  private buildContext(world: World, eid: Eid, ident: Ident): void {
    if (ident.kind === "unit") {
      const chips = el("div", "chips");
      const combat = world.combat.get(eid);
      const hp = world.health.get(eid);
      if (combat) {
        chips.appendChild(chip("sword", String(combat.damage), "Attack damage"));
        // combat.range is stored in WORLD units; show it in TILES (√3·size apart).
        const rangeTiles = combat.range / (Math.sqrt(3) * world.size);
        chips.appendChild(chip("range", rangeTiles.toFixed(1), "Attack range (tiles)"));
      }
      if (hp) chips.appendChild(chip("shield", String(hp.armor), "Armor"));
      const mv = world.move.get(eid);
      if (mv) chips.appendChild(chip("speed", mv.speed.toFixed(1), "Move speed (tiles/s)"));
      const gar = world.garrison.get(eid);
      if (gar) {
        const gchip = chip("users", `${garrisonUsed(world, gar.occupants)}/${gar.capacity}`, "Cargo aboard (population points)");
        chips.appendChild(gchip);
        this.live.garrison = { eid, el: gchip.querySelector("b")!, cap: gar.capacity };
      }
      this.context.appendChild(chips);
      const g = world.gatherer.get(eid);
      if (g) {
        const cb = labeledBar("carry", "package", "carrying");
        this.context.appendChild(cb.el);
        this.live.carry = { eid, cap: g.capacity, ...cb };
      }
      if (world.ident.get(eid)?.cls === "worker") this.buildMenu(world, [eid]);
      if (this.owns(world, eid)) this.context.appendChild(this.stopButton([eid]));
      return;
    }
    if (ident.kind === "resource") {
      const res = world.resource.get(eid)!;
      const chips = el("div", "chips");
      const rname = this.reg.resource(res.resource)?.name ?? res.resource;
      chips.appendChild(chip(RESOURCE_ICON[res.resource] ?? "package", rname, "Resource type"));
      chips.appendChild(chip("clock", res.gatherTime.toFixed(1) + "s", "Gather time per load"));
      const gc = chip("users", "0", "Workers gathering this");
      chips.appendChild(gc);
      this.live.gathererCount = { eid, el: gc.querySelector("b")! };
      this.context.appendChild(chips);
      return;
    }
    // building
    if (!this.owns(world, eid)) return;
    const tags = el("div", "chips");
    const b = world.building.get(eid);
    if (b?.isDropSite) tags.appendChild(chip("package", "Drop-off", "Resource drop-off point"));
    if (b?.populationCap) tags.appendChild(chip("users", "+" + b.populationCap, "Housing provided"));
    if (tags.childElementCount) this.context.appendChild(tags);
    if (!b || b.isFoundation) return;
    // a garrisoned building (a tower): live occupancy + an Ungarrison button. The
    // eject direction defaults to one tile east; right-clicking the ground while
    // the tower is selected lets the player choose where they pour out.
    const gar = world.garrison.get(eid);
    if (gar) {
      const gchips = el("div", "chips");
      const gchip = chip("users", `${garrisonUsed(world, gar.occupants)}/${gar.capacity}`, "Garrisoned units (population points)");
      gchips.appendChild(gchip);
      this.live.garrison = { eid, el: gchip.querySelector("b")!, cap: gar.capacity };
      this.context.appendChild(gchips);
      const ung = el("button", "cmd-btn");
      ung.innerHTML = `<i class="cb-ico">${icon("logOut", 19)}</i><span class="cb-name">Ungarrison</span>`;
      ung.addEventListener("click", () => {
        const t = world.transform.get(eid)!;
        this.cbs.onCommand({ type: "unload", player: this.localPlayer, transport: eid, x: t.pos.x + world.size, y: t.pos.y, queued: false });
      });
      this.context.appendChild(ung);
    }
    // a field is a worked building: show its food stock + assigned farmers
    const fieldRes = world.resource.get(eid);
    if (fieldRes) {
      const chips = el("div", "chips");
      chips.appendChild(chip(RESOURCE_ICON[fieldRes.resource] ?? "package", this.reg.resource(fieldRes.resource)?.name ?? fieldRes.resource, "Harvested here"));
      const gc = chip("users", "0", `Farmers (max ${fieldRes.cap ?? "∞"})`);
      chips.appendChild(gc);
      this.live.gathererCount = { eid, el: gc.querySelector("b")! };
      this.context.appendChild(chips);
      const ab = labeledBar("amount", "wheat", "stock");
      this.context.appendChild(ab.el);
      this.live.amount = { eid, max: fieldRes.max ?? fieldRes.amount, ...ab };
    }
    this.buildProduction(world, eid);
  }

  private buildProduction(world: World, eid: Eid): void {
    const ident = world.ident.get(eid)!;
    const prod = world.producer.get(eid);
    const queue = prod?.queue ?? [];
    if (queue.length) {
      const row = el("div", "queue");
      queue.forEach((q, i) => {
        const slot = el("button", "queue-slot");
        slot.title = `Cancel ${q.unitId}`;
        const u = this.reg.unit(ident.civ!, q.unitId);
        slot.innerHTML = `<i>${icon(CLASS_ICON[u.class] ?? "users", 18)}</i>`;
        if (i === 0) {
          const pb = bar("queue");
          slot.appendChild(pb.el);
          this.live.queueHead = { eid, ...pb };
        }
        slot.addEventListener("click", () =>
          this.cbs.onCommand({ type: "cancelTrain", player: this.localPlayer, building: eid, index: i }));
        row.appendChild(slot);
      });
      this.context.appendChild(row);
    }
    const trainable = this.reg.trainedAt(ident.civ!, ident.defId);
    if (!trainable.length) return;
    const actions = el("div", "actions");
    for (const u of trainable) {
      const btn = el("button", "cmd-btn");
      btn.innerHTML =
        `<i class="cb-ico">${icon(CLASS_ICON[u.class] ?? "users", 19)}</i>` +
        `<span class="cb-name">${u.name}</span>` +
        `<span class="cb-cost">${costHtml(this.reg, u.cost)}</span>`;
      btn.addEventListener("click", () => {
        const miss = this.affordMissing(u.cost, u.population);
        if (miss.length) { this.showToast("Not enough: " + miss.join(", ")); return; }
        this.cbs.onCommand({ type: "train", player: this.localPlayer, building: eid, unitId: u.id });
      });
      this.regCostBtn(btn, u.cost, u.population);
      actions.appendChild(btn);
    }
    this.context.appendChild(actions);
  }

  private buildMenu(world: World, workers: Eid[]): void {
    const civ = world.ident.get(workers[0])?.civ;
    if (!civ) return;
    const actions = el("div", "actions");
    for (const def of this.reg.buildingsOf(civ).filter((b) => !b.isCore)) {
      const bld = def.id;
      const btn = el("button", "cmd-btn");
      btn.innerHTML =
        `<i class="cb-ico">${icon(BUILDING_ICON[bld] ?? "hammer", 19)}</i>` +
        `<span class="cb-name">${def.name}</span>` +
        `<span class="cb-cost">${costHtml(this.reg, def.cost)}</span>`;
      if (this.cbs.buildMode() === bld) btn.classList.add("active");
      btn.addEventListener("click", () => {
        const miss = this.affordMissing(def.cost, 0);
        if (miss.length) { this.showToast("Not enough: " + miss.join(", ")); return; }
        this.cbs.setBuildMode(this.cbs.buildMode() === bld ? null : bld);
      });
      this.regCostBtn(btn, def.cost, 0);
      actions.appendChild(btn);
    }
    this.context.appendChild(actions);
  }

  /** Track a cost/build button so its affordable (red) state updates live. */
  private regCostBtn(btn: HTMLElement, cost: Record<string, number | undefined>, pop: number): void {
    (this.live.costButtons ??= []).push({ btn, cost, pop });
  }

  // ---- per-frame live values --------------------------------------------

  private setBar(ref: BarRef, frac: number, text: string, color?: string): void {
    const f = Math.max(0, Math.min(1, frac));
    ref.fill.style.width = f * 100 + "%";
    if (color) ref.fill.style.background = color;
    ref.text.textContent = text;
  }

  private refreshLive(world: World): void {
    const L = this.live;
    if (L.hp) {
      const hp = world.health.get(L.hp.eid);
      if (hp) {
        const frac = hp.hp / hp.maxHp;
        this.setBar(L.hp, frac, `${Math.ceil(hp.hp)} / ${hp.maxHp}`, hpColor(frac));
      }
    }
    if (L.construction) {
      const b = world.building.get(L.construction.eid);
      if (b) this.setBar(L.construction, b.constructionProgress, `Building ${Math.round(b.constructionProgress * 100)}%`);
    }
    if (L.amount) {
      const r = world.resource.get(L.amount.eid);
      if (r) this.setBar(L.amount, r.amount / L.amount.max, `${Math.ceil(r.amount)} left`);
    }
    if (L.carry) {
      const g = world.gatherer.get(L.carry.eid);
      if (g) this.setBar(L.carry, g.carrying / L.carry.cap, `${Math.min(L.carry.cap, Math.round(g.carrying))} / ${L.carry.cap}`);
    }
    if (L.queueHead) {
      const q = world.producer.get(L.queueHead.eid)?.queue[0];
      if (q) this.setBar(L.queueHead, 1 - q.time / q.total, Math.ceil(q.time) + "s");
    }
    if (L.gathererCount) {
      let n = 0;
      for (const [, g] of world.gatherer) if (g.node === L.gathererCount.eid) n++;
      L.gathererCount.el.textContent = String(n);
    }
    if (L.garrison) {
      const gar = world.garrison.get(L.garrison.eid);
      if (gar) L.garrison.el.textContent = `${garrisonUsed(world, gar.occupants)}/${L.garrison.cap}`;
    }
    if (L.status) {
      const txt = unitStatus(world, L.status.eid, this.reg);
      L.status.el.textContent = txt;
      L.status.wrap.classList.toggle("idle", txt === "Idle");
    }
    if (L.builders || L.timeLeft) {
      const eid = (L.builders ?? L.timeLeft)!.eid;
      let n = 0;
      for (const [, bd] of world.builder) if (bd.foundation === eid) n++;
      if (L.builders) L.builders.el.textContent = String(n);
      if (L.timeLeft) {
        const b = world.building.get(eid);
        const rate = (1 / L.timeLeft.buildTime) * Math.min(n, 3);
        const left = b && n > 0 ? (1 - b.constructionProgress) / rate : Infinity;
        L.timeLeft.el.textContent = isFinite(left)
          ? `${Math.floor(left / 60)}:${String(Math.ceil(left % 60)).padStart(2, "0")}`
          : "—";
      }
    }
    // red out any train/build button the player can't currently afford
    if (L.costButtons) {
      for (const cb of L.costButtons) {
        const miss = this.affordMissing(cb.cost, cb.pop);
        cb.btn.classList.toggle("unaffordable", miss.length > 0);
        cb.btn.title = miss.length ? "Not enough: " + miss.join(", ") : "";
      }
    }
  }
}
